import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Loader2, DollarSign, CalendarDays, CheckCircle2, AlertCircle, MapPin, Sparkles, UserCheck, Landmark } from "lucide-react";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { api, unwrap } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { formatDate } from "@/lib/format";
import { Avatar } from "@/pages/admin/AdminCleaners";

function StatCard({ icon: Icon, label, value, accent, delta }) {
  const deltaColor =
    delta == null ? "text-[#9CA3AF]" :
    delta > 0 ? "text-emerald-600" :
    delta < 0 ? "text-rose-600" : "text-[#9CA3AF]";
  const arrow = delta == null ? "" : delta > 0 ? "↑" : delta < 0 ? "↓" : "→";
  return (
    <Card className="rounded-2xl p-5 border border-[#E5E7EB]">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-[#4B5563] font-semibold">{label}</div>
          <div className="font-mono text-3xl font-bold mt-2">{value}</div>
          {delta !== undefined && (
            <div className={`text-xs font-semibold mt-1 ${deltaColor}`} data-testid={`stat-delta-${label.toLowerCase().replace(/\s+/g, "-")}`}>
              {delta == null ? "—" : `${arrow} ${Math.abs(delta).toFixed(1)}% vs prev`}
            </div>
          )}
        </div>
        <span className={`w-10 h-10 rounded-xl grid place-items-center ${accent}`}><Icon className="w-5 h-5" /></span>
      </div>
    </Card>
  );
}

const STATUS_BADGE = {
  pending_payment: "bg-amber-50 text-amber-700 border-amber-200",
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  assigned: "bg-sky-50 text-sky-700 border-sky-200",
  in_progress: "bg-indigo-50 text-indigo-700 border-indigo-200",
  completed: "bg-teal-50 text-teal-700 border-teal-200",
  cancelled: "bg-stone-100 text-stone-600 border-stone-200",
  refunded: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function AdminDashboard() {
  const { locale } = useI18n();
  const [stats, setStats] = useState(null);
  const [chart, setChart] = useState([]);
  const [recent, setRecent] = useState([]);
  const [quick, setQuick] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, c, b, q] = await Promise.all([
          api.get("/admin/dashboard/stats"),
          api.get("/admin/dashboard/revenue-chart"),
          api.get("/admin/bookings?page=1&page_size=10"),
          api.get("/admin/dashboard/quick-stats"),
        ]);
        setStats(unwrap(s));
        setChart(unwrap(c).series || []);
        setRecent(unwrap(b).items || []);
        setQuick(unwrap(q));
      } finally {
        setLoading(false);
      }
    })();
    api.get("/admin/payouts/summary").then((r) => setPayouts(unwrap(r))).catch(() => {});
  }, []);

  const totalRevenue = useMemo(() => chart.reduce((sum, p) => sum + (p.revenue || 0), 0), [chart]);

  if (loading) return <div className="grid place-items-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tighter">Dashboard</h1>
        <p className="text-[#4B5563] text-sm mt-1">A live snapshot of bookings and revenue.</p>
      </div>

      {payouts?.needs_attention > 0 && (
        <Card className="rounded-2xl p-5 border border-amber-300 bg-amber-50" data-testid="payouts-attention-card">
          <div className="flex items-start gap-3">
            <span className="w-10 h-10 rounded-xl bg-amber-200 text-amber-800 grid place-items-center shrink-0">
              <Landmark className="w-5 h-5" />
            </span>
            <div className="min-w-0">
              <div className="font-semibold text-amber-900" data-testid="payouts-attention-count">
                {payouts.needs_attention} cleaner payout{payouts.needs_attention > 1 ? "s" : ""} need{payouts.needs_attention > 1 ? "" : "s"} attention
              </div>
              <div className="text-sm text-amber-800 mt-0.5">
                {payouts.held} held · {payouts.failed} failed. Open the booking and press "Retry payout" once the cause is fixed.
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {(payouts.attention_items || []).map((it) => (
                  <Link key={it.booking_id} to={`/admin/bookings/${it.booking_id}`}
                    className="text-xs font-mono px-2.5 py-1 rounded-lg bg-white border border-amber-300 text-amber-900 hover:bg-amber-100"
                    title={it.reason_label} data-testid={`payout-attention-${it.booking_number}`}>
                    #{it.booking_number} · ${Number(it.amount || 0).toFixed(2)}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={DollarSign} label="Revenue today" value={`$${(stats?.revenue.today || 0).toFixed(2)}`}
          accent="bg-[#0EA5A4]/10 text-[#0F766E]" delta={stats?.comparison?.revenue_today_vs_yesterday} />
        <StatCard icon={DollarSign} label="Revenue (month)" value={`$${(stats?.revenue.month || 0).toFixed(2)}`}
          accent="bg-[#FACC15]/15 text-[#0C1A1A]" delta={stats?.comparison?.revenue_month_vs_prev} />
        <StatCard icon={CalendarDays} label="Bookings (month)" value={stats?.bookings.month || 0}
          accent="bg-sky-50 text-sky-700" delta={stats?.comparison?.bookings_month_vs_prev} />
        <StatCard icon={CheckCircle2} label="Completed" value={stats?.bookings.completed || 0}
          accent="bg-emerald-50 text-emerald-700" />
      </motion.div>

      <Card className="rounded-2xl p-5 border border-[#E5E7EB]">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-[#4B5563] font-semibold">Revenue · last 30 days</div>
            <div className="font-mono text-2xl font-bold">${totalRevenue.toFixed(2)}</div>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={chart}>
            <defs>
              <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0EA5A4" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#0EA5A4" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} fontSize={11} stroke="#9CA3AF" />
            <YAxis fontSize={11} stroke="#9CA3AF" />
            <Tooltip cursor={{ stroke: "#0EA5A4", strokeWidth: 1, strokeOpacity: 0.3 }}
              contentStyle={{ borderRadius: 12, border: "1px solid #E5E7EB", fontSize: 12 }}
              formatter={(v) => `$${Number(v).toFixed(2)}`} />
            <Area type="monotone" dataKey="revenue" stroke="#0F766E" strokeWidth={2.5} fill="url(#revGrad)" />
          </AreaChart>
        </ResponsiveContainer>
      </Card>

      {quick && (quick.top_zones.length > 0 || quick.top_tasks.length > 0 || (quick.top_cleaners || []).length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="quick-stats">
          <Card className="rounded-2xl p-5 border border-[#E5E7EB]">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-8 h-8 rounded-lg bg-[#0EA5A4]/10 grid place-items-center text-[#0F766E]"><MapPin className="w-4 h-4" /></span>
              <h3 className="font-display font-bold">Top zones</h3>
            </div>
            {quick.top_zones.length === 0 ? (
              <p className="text-sm text-[#9CA3AF] py-4">No paid bookings yet.</p>
            ) : (
              <ol className="space-y-2">
                {quick.top_zones.map((z, i) => (
                  <li key={i} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-[#9CA3AF] w-4">{i + 1}.</span>
                      <span className="font-semibold">{z.name}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-[#4B5563]">{z.count} bookings</span>
                      <span className="font-mono font-semibold text-[#0EA5A4]">${z.revenue.toFixed(2)}</span>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </Card>

          <Card className="rounded-2xl p-5 border border-[#E5E7EB]">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-8 h-8 rounded-lg bg-[#FACC15]/20 grid place-items-center text-[#0C1A1A]"><Sparkles className="w-4 h-4" /></span>
              <h3 className="font-display font-bold">Top extras</h3>
            </div>
            {quick.top_tasks.length === 0 ? (
              <p className="text-sm text-[#9CA3AF] py-4">No extras booked yet.</p>
            ) : (
              <ol className="space-y-2">
                {quick.top_tasks.map((t, i) => (
                  <li key={i} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-[#9CA3AF] w-4">{i + 1}.</span>
                      <span className="font-semibold capitalize">{t.code.replace(/_/g, " ")}</span>
                    </div>
                    <span className="text-xs text-[#4B5563]">{t.count} times</span>
                  </li>
                ))}
              </ol>
            )}
          </Card>

          <Card className="rounded-2xl p-5 border border-[#E5E7EB]">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-8 h-8 rounded-lg bg-sky-50 grid place-items-center text-sky-700"><UserCheck className="w-4 h-4" /></span>
              <h3 className="font-display font-bold">Top cleaners</h3>
            </div>
            {(quick.top_cleaners || []).length === 0 ? (
              <p className="text-sm text-[#9CA3AF] py-4">No assignments this month.</p>
            ) : (
              <ol className="space-y-2">
                {quick.top_cleaners.map((c, i) => (
                  <li key={c.id || i} className="flex items-center justify-between text-sm">
                    <Link to="/admin/cleaners" className="flex items-center gap-2 min-w-0 hover:underline" data-testid={`top-cleaner-${i}`}>
                      <span className="font-mono text-xs text-[#9CA3AF] w-4">{i + 1}.</span>
                      <Avatar src={c.photo_url} name={c.name} size={28} />
                      <span className="font-semibold truncate">{c.name}</span>
                    </Link>
                    <span className="text-xs text-[#4B5563] shrink-0">{c.count} bookings</span>
                  </li>
                ))}
              </ol>
            )}
          </Card>
        </div>
      )}

      <Card className="rounded-2xl border border-[#E5E7EB] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
          <h3 className="font-display font-bold">Recent bookings</h3>
          <Link to="/admin/bookings" className="text-xs text-[#0EA5A4] font-semibold hover:underline">View all</Link>
        </div>
        {recent.length === 0 ? (
          <div className="p-10 text-center text-[#4B5563]">
            <AlertCircle className="w-6 h-6 mx-auto mb-2 text-[#9CA3AF]" />
            No bookings yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="recent-bookings-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-[#4B5563]">
                  <th className="px-5 py-3">#</th>
                  <th className="px-5 py-3">Customer</th>
                  <th className="px-5 py-3">Zone</th>
                  <th className="px-5 py-3">Total</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((b) => (
                  <tr key={b.id} className="border-t border-[#E5E7EB] hover:bg-[#FAFAF9]">
                    <td className="px-5 py-3 font-mono">
                      <Link to={`/admin/bookings/${b.id}`} className="text-[#0EA5A4] hover:underline">{b.booking_number}</Link>
                    </td>
                    <td className="px-5 py-3">{b.customer.full_name} <span className="text-[#9CA3AF]">· {b.customer.email}</span></td>
                    <td className="px-5 py-3">{b.service.zone_name}</td>
                    <td className="px-5 py-3 font-mono font-semibold">${b.pricing.total.toFixed(2)}</td>
                    <td className="px-5 py-3"><Badge className={`border ${STATUS_BADGE[b.status]} font-medium capitalize`}>{b.status.replace("_", " ")}</Badge></td>
                    <td className="px-5 py-3 text-[#4B5563]">{formatDate(b.created_at, locale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
